import { ChangeDetectionStrategy, Component, DestroyRef, inject, Injector, OnInit } from '@angular/core';
import { AppService } from '@core/services/app.service';
import { takeWhile, tap } from 'rxjs/operators';
import { BaseComponent } from './shared/components/base.component';
import { TopNavComponent } from './shared/components/top-nav/top-nav.component';
import { RouterOutlet } from '@angular/router';
import { FooterComponent } from './shared/components/footer/footer.component';
import { SessionService } from '@core/services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IdleTimeoutService } from '@core/services/idle-timer.service';
import { MatDialogRef } from '@angular/material/dialog';
import { SessionExpiryDialogComponent } from './shared/components/session-expiry-dialog/session-expiry-dialog.component';
import { InactivityLogoutBannerComponent } from './shared/components/inactivity-logout-banner/inactivity-logout-banner.component';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TopNavComponent, RouterOutlet, FooterComponent, InactivityLogoutBannerComponent]
})
export class AppComponent extends BaseComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    pageReady = false;
    buildVersion = '5be714bd2';
    private dialogRef?: MatDialogRef<SessionExpiryDialogComponent>;

    constructor(
        private readonly injector: Injector,
        private readonly appService: AppService,
        private readonly sessionService: SessionService,
        private readonly idleTimeoutService: IdleTimeoutService
    ) {
        super(injector);
    }

    ngOnInit() {
        super.ngOnInit();
        this.translateDatepicker();
        this.createAPICache();

        this.sessionService.isAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isAuthenticated => {
            if (isAuthenticated) {
                this.idleTimeoutService.initialize(environment.idleTimeoutMinutes, environment.warningTimeoutMinutes);
                this.idleTimeoutService.startTimer();
            } else {
                this.idleTimeoutService.stopTimer();
            }
        });

        this.idleTimeoutService.idleTimeout$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.showWarningDialog();
        });

        this.idleTimeoutService.warningTimeout$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.goToLogout();
        });

        this.idleTimeoutService.closeWarningDialog$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.dialogRef?.close();
        });
    }

    private translateDatepicker() {
        this.languageService
            .translateDatepicker()
            .pipe(takeWhile(() => this.alive))
            .subscribe();
    }

    private createAPICache() {
        this.appService
            .createAPICache(this.buildVersion)
            .pipe(
                takeWhile(() => this.alive),
                tap(() => this.renderApp()),
                tap(() => (this.cache.buildVersion = this.buildVersion))
            )
            .subscribe();
    }

    private renderApp() {
        this.loadingService.stopLoading();
        this.pageReady = true;
        this.cd();
    }

    private showWarningDialog() {
        const totalSeconds = Math.ceil(this.idleTimeoutService.warningTime / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const translations = this.cache[this.languageService.languageType];
        const durationParts: string[] = [];
        if (minutes > 0) {
            durationParts.push(this.utils.format(translations[minutes === 1 ? 'time.minute' : 'time.minutes'], [String(minutes)]));
        }
        if (seconds > 0 || minutes === 0) {
            durationParts.push(this.utils.format(translations[seconds === 1 ? 'time.second' : 'time.seconds'], [String(seconds)]));
        }

        this.dialogRef = this.dialogService.instance(
            SessionExpiryDialogComponent,
            { countdown$: this.idleTimeoutService.warningCountdown, openingDuration: durationParts.join(' ') },
            {
                disableClose: true,
                role: 'alertdialog',
                panelClass: ['my-dialog', 'session-expiry-dialog'],
                autoFocus: '#session-expiry-continue',
                ariaLabelledBy: 'session-expiry-title',
                ariaDescribedBy: 'session-expiry-description'
            }
        );

        this.dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                this.dialogRef = undefined;
                if (result === 'continue') {
                    this.resetIdleAndStopWarning();
                } else if (result === 'logout') {
                    this.goToLogout();
                }
            });
    }

    private resetIdleAndStopWarning() {
        this.idleTimeoutService.continueSession();
    }

    private goToLogout() {
      this.sessionService.markAutoLogout();
      this.sessionService.logout();
    }
}
